import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { CanAccess } from "ra-core";
import { Gift, RefreshCw } from "lucide-react";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import type { Agency } from "../types";
import { CounterTile, GrantFilmsDialog } from "./GrantFilmsDialog";
import {
  fetchAgencyOrders,
  fetchAgencyWallet,
  formatBillingDate,
  formatMinor,
  planSummary,
  studioBillingErrorMessage,
  studioBillingKeys,
  type StudioOrder,
  type StudioWallet,
} from "./studioBillingClient";

const RECENT_ORDERS = 10;

/**
 * An agency's Studio billing, read live from the bot's wallet in this CRM's
 * environment (bot docs/studio-billing.md): films available and per counter,
 * debt, the monthly plan, the agency's price book and recent orders. CRM
 * administrators can also grant films (GrantFilmsDialog). When billing is off
 * in the environment the bot answers `{enabled:false}` and nothing is metered.
 */
export const AgencyBillingSection = ({ agency }: { agency: Agency }) => {
  const agencyId = String(agency.id);
  const [grantOpen, setGrantOpen] = useState(false);
  const wallet = useQuery({
    queryKey: studioBillingKeys.wallet(agencyId),
    queryFn: () => fetchAgencyWallet(agencyId),
    staleTime: 30_000,
    retry: false,
  });
  const orders = useQuery({
    queryKey: studioBillingKeys.orders(agencyId),
    queryFn: () => fetchAgencyOrders(agencyId),
    staleTime: 30_000,
    retry: false,
  });
  const enabled = wallet.data?.enabled === true;
  const refreshing = wallet.isFetching || orders.isFetching;

  return (
    <section data-testid="agency-billing">
      <div className="mb-1 flex items-center justify-between">
        <h3 className="text-sm font-medium">Billing</h3>
        <div className="flex items-center gap-1">
          <Button
            size="sm"
            variant="ghost"
            aria-label="Refresh billing"
            disabled={refreshing}
            onClick={() => {
              void wallet.refetch();
              void orders.refetch();
            }}
          >
            <RefreshCw
              className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`}
            />
          </Button>
          <CanAccess resource="agency_billing" action="create">
            <Button
              size="sm"
              variant="outline"
              disabled={!enabled}
              title={
                enabled ? undefined : "Billing is not readable here right now"
              }
              onClick={() => setGrantOpen(true)}
            >
              <Gift className="mr-1 h-4 w-4" /> Grant films
            </Button>
          </CanAccess>
        </div>
      </div>
      <p className="mb-2 text-xs text-muted-foreground">
        Read live from the bot&apos;s Studio wallet in this CRM&apos;s
        environment. Films are spent free first, then plan, then bought.
      </p>

      {wallet.isPending ? (
        <p className="text-sm text-muted-foreground">Loading billing…</p>
      ) : wallet.isError ? (
        <Alert variant="destructive">
          <AlertDescription className="flex flex-wrap items-center gap-2">
            {studioBillingErrorMessage(wallet.error)}
            <Button
              size="sm"
              variant="outline"
              onClick={() => wallet.refetch()}
            >
              Retry
            </Button>
          </AlertDescription>
        </Alert>
      ) : !wallet.data.enabled ? (
        <p className="text-sm text-muted-foreground">
          Billing is off in this environment: the bot does not meter studio
          films here and the portal shows no billing UI, so there is nothing to
          grant.
        </p>
      ) : (
        <WalletDetails
          wallet={wallet.data}
          orders={orders.data}
          ordersError={orders.isError ? orders.error : null}
          ordersPending={orders.isPending}
        />
      )}

      {enabled && (
        <GrantFilmsDialog
          agency={agency}
          open={grantOpen}
          onOpenChange={setGrantOpen}
        />
      )}
    </section>
  );
};

const WalletDetails = ({
  wallet,
  orders,
  ordersError,
  ordersPending,
}: {
  wallet: StudioWallet;
  orders: StudioOrder[] | undefined;
  ordersError: unknown;
  ordersPending: boolean;
}) => {
  const price = formatMinor(wallet.price.amount_minor, wallet.price.currency);
  const introEnds = formatBillingDate(wallet.intro?.ends_at);
  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-1">
        <Badge variant="secondary">
          {wallet.billing_mode === "exempt" ? "premiere: unmetered" : "metered"}
        </Badge>
        {wallet.agency_status === "paused" && (
          <Badge variant="destructive">paused</Badge>
        )}
        {wallet.debt > 0 && (
          <Badge variant="destructive">
            debt blocks new films until it is paid
          </Badge>
        )}
      </div>

      <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
        <CounterTile
          label="Films available"
          value={wallet.films_available}
          title="Free + bought + plan films that still count (debt is checked separately)."
        />
        <CounterTile
          label="Free"
          value={wallet.free_credits}
          title="Starter films, comps and goodwill. Never unlock “Powered by” removal."
        />
        <CounterTile
          label="Plan"
          value={wallet.plan_credits}
          title="Monthly plan films still usable. They lapse after a plan ends."
        />
        <CounterTile
          label="Bought"
          value={wallet.paid_credits}
          title="Packs and invoiced deals. Never expire."
        />
        <CounterTile
          label="Debt"
          value={wallet.debt}
          title="Films owed after a dispute or refund voided films already used. Blocks new films."
          tone={wallet.debt > 0 ? "destructive" : undefined}
        />
      </div>

      <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-sm">
        <dt className="text-muted-foreground">Plan</dt>
        <dd>{planSummary(wallet.plan)}</dd>
        <dt className="text-muted-foreground">Price book</dt>
        <dd className="flex flex-wrap items-center gap-2">
          <span className="font-mono text-xs">
            {wallet.price_book_id ?? "none (legacy single price)"}
          </span>
          {price && (
            <span className="text-muted-foreground">
              {wallet.price.credits > 1
                ? `${price} for ${wallet.price.credits} films`
                : `${price} a film`}
            </span>
          )}
          {wallet.intro && (
            <Badge variant="outline">
              {wallet.intro.label}
              {introEnds ? ` until ${introEnds}` : ""}
            </Badge>
          )}
        </dd>
      </dl>

      <div>
        <h4 className="mb-1 text-xs font-medium text-muted-foreground">
          Recent orders
        </h4>
        {ordersPending ? (
          <p className="text-sm text-muted-foreground">Loading orders…</p>
        ) : ordersError ? (
          <p className="text-sm text-destructive">
            {studioBillingErrorMessage(ordersError)}
          </p>
        ) : !orders || orders.length === 0 ? (
          <p className="text-sm text-muted-foreground">No orders yet.</p>
        ) : (
          <OrdersTable orders={orders} />
        )}
      </div>
    </div>
  );
};

const OrdersTable = ({ orders }: { orders: StudioOrder[] }) => {
  const recent = [...orders]
    .sort((a, b) => (b.created_at ?? "").localeCompare(a.created_at ?? ""))
    .slice(0, RECENT_ORDERS);
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Date</TableHead>
          <TableHead className="text-right">Films</TableHead>
          <TableHead className="text-right">Amount</TableHead>
          <TableHead>Type</TableHead>
          <TableHead>Status</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {recent.map((order) => (
          <TableRow key={order.order_id || order.created_at}>
            <TableCell>{formatBillingDate(order.created_at) ?? "-"}</TableCell>
            <TableCell className="text-right tabular-nums">
              {order.films ?? "-"}
            </TableCell>
            <TableCell className="text-right tabular-nums">
              {formatMinor(order.amount_minor, order.currency) ?? "-"}
            </TableCell>
            <TableCell>{order.kind === "plan" ? "Plan" : "Pack"}</TableCell>
            <TableCell>
              <Badge variant={orderStatusVariant(order.status)}>
                {order.status}
              </Badge>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

function orderStatusVariant(
  status: string,
): "secondary" | "destructive" | "outline" {
  if (status === "paid") return "secondary";
  if (/refund|dispute|void|fail/.test(status)) return "destructive";
  return "outline";
}
